import { differenceInDays } from 'date-fns/differenceInDays';
import * as Yup from 'yup';
import { fixGoDate } from '../helpers/fix-go-types';

export const BounceRecordSchema = Yup.object().shape({
  at: Yup.date().required().transform(fixGoDate),
  feedback_id: Yup.string().default(''),
  type: Yup.string().default(''),
  sub_type: Yup.string().default(''),
  diagnostic: Yup.string().default(''),
});

export type BounceRecord = Yup.InferType<typeof BounceRecordSchema>;

// form validation rules
export const RepresentativeSchema = Yup.object().shape({
  first_name: Yup.string(),
  last_name: Yup.string(),

  division: Yup.string().defined(),

  email: Yup.string().email().required(),

  send_order: Yup.boolean().required(),

  // Phase 3: bounce state. Both fields are populated by the bounce
  // handler (lambdas/api-worker/handle_bounce.go) when SES reports a
  // permanent bounce. Safe defaults so older API responses don't
  // break the schema during deploy.
  last_permanent_bounce: Yup.date().nullable().default(null).transform(fixGoDate),
  bounce_history: Yup.array().of(BounceRecordSchema.required()).default([]),
});

export type Representative = Yup.InferType<typeof RepresentativeSchema>;

// isDisabledByBounce mirrors VendorRepresentative.IsDisabledByBounce()
// on the Go side. Effective-can-email = send_order && !isDisabledByBounce(r).
export function isDisabledByBounce(rep: Representative): boolean {
  return rep.last_permanent_bounce != null;
}

// BOUNCE_REENABLE_WINDOW_DAYS is the lookback window for detecting
// "globally suppressed" addresses: a new permanent bounce within this
// many days of any prior bounce means the address is on AWS's global
// suppression list (which we can't remove) and re-enabling won't help.
// Tracks the design doc's "90-day re-bounce detection."
export const BOUNCE_REENABLE_WINDOW_DAYS = 90;

// isGloballySuppressed reports whether the rep's most recent bounce
// repeats a prior bounce within BOUNCE_REENABLE_WINDOW_DAYS. When true,
// the UI hides the Re-enable button and tells the user to update the
// address instead — deleting from AWS's account suppression list won't
// recover an address that's on the global list.
export function isGloballySuppressed(rep: Representative): boolean {
  if (rep.bounce_history.length < 2) {
    return false;
  }

  const last = rep.bounce_history[rep.bounce_history.length - 1];

  for (let i = 0; i < rep.bounce_history.length - 1; i++) {
    const prior = rep.bounce_history[i];
    if (differenceInDays(last.at, prior.at) <= BOUNCE_REENABLE_WINDOW_DAYS) {
      return true;
    }
  }

  return false;
}

// bounceReasonForSubType maps an SES bounce sub_type to a plain-English
// reason. Shared by lastBounceReason (vendor edit) and the order
// delivery banner (order summary) so the two never drift. Falls back to
// a generic line so raw SMTP diagnostics never reach store owners.
export function bounceReasonForSubType(subType: string): string {
  switch (subType) {
    case 'NoEmail':
      return "This address doesn't exist";
    case 'Suppressed':
    case 'OnAccountSuppressionList':
      return 'This address was blocked after repeated delivery failures';
    case 'MailboxFull':
      return "The recipient's mailbox is full";
    default:
      return "Email couldn't be delivered";
  }
}

// lastBounceReason returns a plain-English reason for the most recent
// bounce, derived from SES's bounce sub_type. Falls back to a generic
// "couldn't be delivered" line for missing or unknown sub_types so the
// dialog never surfaces raw SMTP diagnostics to store owners.
export function lastBounceReason(rep: Representative): string {
  if (rep.bounce_history.length === 0) {
    return "Email couldn't be delivered";
  }

  return bounceReasonForSubType(rep.bounce_history[rep.bounce_history.length - 1].sub_type);
}
